// StepsView.tsx
import React, { Suspense, lazy, useEffect, useRef } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Header from '../shared/header';
import Button from '../shared/button';
import { useGlobalState } from '../../providers/globalState/GlobalStateProvider';
import ScrollToTop from '../shared/scrollToTop';
import { CREATING_PROGRAM_VERSION } from '../../providers/setting';
import Loader from '../shared/loader';
import { usePosthog } from '../../providers/posthog/PosthogProvider';
import { useStepPreloader } from '../../hooks/useStepPreloader';

// Lazy-loaded step components
const Language = lazy(() => import('./language'));
const NameFilling = lazy(() => import('./nameFilling'));
const TopicsOfInterest = lazy(() => import('./topicsOfInterest'));
const PersonalPlan = lazy(() => import('./personalPlan'));
const InGoodHands = lazy(() => import('./inGoodHands'));
const GoalPractice = lazy(() => import('./goalPractice'));
const AskAboutTest = lazy(() => import('./askAboutTest'));
const VocabularyTest = lazy(() => import('./vocabularyTest'));
const GrammarTest = lazy(() => import('./grammarTest'));
const LexTest = lazy(() => import('./lexTest'));
const LexTestResults = lazy(() => import('./lexTestResults'));
const TestResults = lazy(() => import('./testResults'));
const BuildLearningPlan = lazy(() => import('./buildLearningPlan'));
const CreatingProgram = lazy(() => import('./creatingProgram'));
const CreatingProgramV2 = lazy(() => import('./creatingProgramV2'));
const Email = lazy(() => import('./emailView'));
const ExpectView = lazy(() => import('./expectView'));
const SingleChoiceView = lazy(() => import('../shared/singleChoiceView').then(m => ({ default: m.SingleChoiceView })));
const StruggleView = lazy(() => import('./struggleView'));
const StrugglesView = lazy(() => import('./struggles'));
const GenderSelection = lazy(() => import('./genderSelection'));
const Age = lazy(() => import('./age'));
const Summary = lazy(() => import('./summary'));
const AgeV2 = lazy(() => import('./ageV2'));
const HowMultiView = lazy(() => import('./howMulti'));
const LearningEfficiency = lazy(() => import('./learningEfficiency'));
const LevelUp = lazy(() => import('./levelUp'));
const StartStep = lazy(() => import('./startStep'));
const ToeflChanges = lazy(() => import('./toeflChanges'));
const ToeflChangesView = lazy(() => import('./toeflChangesView'));
const ToeflPractice = lazy(() => import('./toeflPractice'));
const SuccessRedirect = lazy(() => import('./successRedirect'));

// Heavy payment components - loaded separately
const ChoosePlaneWithPayment = lazy(() =>
    import('../../providers/paymentProvider/TOEFLArenaPaymentProvider').then(({ TOEFLArenaPaymentProvider }) =>
        import('./choosePlane').then(({ default: ChoosePlane }) => ({
            default: () => (
                <TOEFLArenaPaymentProvider>
                    <ChoosePlane />
                </TOEFLArenaPaymentProvider>
            )
        }))
    )
);




function StepsView() {
    const { isDarkView, currentStep } = useGlobalState();
    const location = useLocation();
    const isStandaloneSuccess = location.pathname === '/success';
    const { capture } = usePosthog();
    const lastStepRef = useRef<string | null>(null);

    useStepPreloader();

    useEffect(() => {
        capture('la_funnel_page_view', {
            path: location.pathname,
            step: currentStep
        });
    }, [location.pathname, currentStep, capture]);

    useEffect(() => {
        if (!currentStep || currentStep === lastStepRef.current) {
            return;
        }

        lastStepRef.current = currentStep;
        capture('la_funnel_step_view', {
            step: currentStep
        });

        if (currentStep === 'choosePlan') {
            capture('la_funnel_paywall_view');
        }
        if (currentStep === 'testResults') {
            capture('la_funnel_vocab_results_view');
        }
        if (currentStep === 'grammarTestResults') {
            capture('la_funnel_grammar_results_view');
        }
        if (currentStep === 'lexTestResults') {
            capture('la_funnel_lex_results_view');
        }
        if (currentStep === 'summary') {
            capture('la_funnel_summary_view');
        }
    }, [currentStep, capture]);

    return (
        <div className={isDarkView ? "App app-dark" : `App ${currentStep === "choosePlan" ? "paywall" : ""}`}>
            {!isStandaloneSuccess && <Header />}
            <ScrollToTop />
            <Suspense fallback={<Loader />}>
                <Routes>
                    <Route path="/" element={<Navigate to={`/start`} />} />
                    <Route path="/start" element={ <StartStep />} />
                    <Route path="/gender" element={<GenderSelection />} />
                    <Route path="/age" element={<Age />} />
                    <Route path="/language" element={<Language />} />
                    {/* <Route path="/video" element={<VideoStep />} /> */}
                    <Route path="/video" element={<Navigate to="/name" />} />
                    <Route path="/name" element={<NameFilling />} />
                    <Route path="/toeflChanges" element={<ToeflChanges />} />
                    <Route path="/toeflChangesView/:index" element={<ToeflChangesView />} />
                    <Route path="/personalPlan" element={<PersonalPlan />} />
                    <Route path="/why" element={<SingleChoiceView type="whys" />} />
                    <Route path="/how" element={<HowMultiView />} />
                    <Route path="/learningEfficiencyFocus" element={<LearningEfficiency type='focus'/>} />
                    <Route path="/level" element={<SingleChoiceView type="levels" />} />
                    <Route path="/struggles" element={<StrugglesView />} />
                    <Route path="/struggleView/:id" element={<StruggleView />} />
                    <Route path="/learningEfficiencyRegularStudy" element={<LearningEfficiency type='regularStudy'/>} />
                    <Route path="/topics" element={<TopicsOfInterest />} />
                    <Route path="/goal" element={<GoalPractice />} />
                    <Route path="/askTest" element={<AskAboutTest />} />
                    <Route path="/toeflPractice" element={<ToeflPractice />} />
                    <Route path="/vocabularyTestBegginer" element={<VocabularyTest />} />
                    <Route path="/vocabularyTestIntermediate" element={<VocabularyTest />} />
                    <Route path="/vocabularyTestAdvanced" element={<VocabularyTest />} />
                    <Route path="/grammarTest/:questionIndex" element={<GrammarTest />} />
                    <Route path="/testResults" element={<TestResults />} />
                    <Route path="/grammarTestResults" element={<TestResults />} />
                    <Route path="/lexTest/:questionIndex" element={<LexTest />} />
                    <Route path="/lexTestResults" element={<LexTestResults />} />
                    <Route path="/planBuild" element={<BuildLearningPlan />} />
                    <Route path="/creatProgram" element={CREATING_PROGRAM_VERSION === 'v2' ? <CreatingProgramV2 /> : <CreatingProgram />} />
                    <Route path="/email" element={<Email />} />
                    <Route path="/expect1Week" element={<ExpectView />} />
                    <Route path="/expect4Week" element={<ExpectView />} />
                    <Route path="/expect12Week" element={<ExpectView />} />
                    <Route path="/expectMonth12" element={<ExpectView />} />
                    <Route path="/goodHands1" element={<InGoodHands />} />
                    <Route path="/goodHands2" element={<InGoodHands />} />
                    <Route path="/summary" element={<Summary/>} />
                    <Route path="/levelUp" element={<LevelUp/>} />
                    {/* <Route path="/levelUp" element={<Navigate to="/choosePlan" />} /> */}
                    <Route path="/choosePlan" element={<ChoosePlaneWithPayment />} />
                    <Route path="/success" element={<SuccessRedirect />} />
                </Routes>
            </Suspense>
            {!isStandaloneSuccess && <Button />}
            {/* <Cookies/> */}
        </div>
    );
}

export default StepsView;
